import { type NewPushSubscription, subscriptionId } from "@larder/shared";
import { collection, deleteDoc, doc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Repository for push subscriptions.
 *
 * Under `users/{uid}` rather than under a location, because a subscription
 * belongs to a person's device, not to a household. Someone in two households
 * has one phone and should get one reminder from whichever of them needs it;
 * storing it per location would ring the same phone twice.
 */

const subsCol = (uid: string) => collection(db, "users", uid, "pushSubs");

/**
 * Save a subscription, keyed by a hash of its endpoint.
 *
 * `setDoc` with a derived id, not `addDoc`, and that is the whole reliability
 * story for this collection: the app re-subscribes on every launch, so a random
 * id would add a document per launch and the check-in would arrive once per
 * launch the device had ever made. Keyed by endpoint, a re-subscribe is a
 * no-op write. See `subscriptionId` in shared.
 *
 * `merge: true`, and the type refuses `lastOkAt`, for the same underlying
 * reason: the sender owns that field. A plain overwrite here would reset the
 * server's delivery record to nothing every time the app launched, which is
 * both a lost diagnostic and a lie.
 */
export async function saveSubscription(
  uid: string,
  record: NewPushSubscription,
): Promise<string> {
  const id = subscriptionId(record.endpoint);
  await setDoc(doc(subsCol(uid), id), record, { merge: true });
  return id;
}

/** Forget one device — from Settings, or after the browser drops it. */
export async function removeSubscription(uid: string, endpoint: string): Promise<void> {
  await deleteDoc(doc(subsCol(uid), subscriptionId(endpoint)));
}

/**
 * How many devices this account can currently be reached on.
 *
 * Read once, on demand, rather than kept in a listener: it changes when a user
 * installs the app on a new phone, which is not an event this screen needs to
 * react to live, and a standing listener on every session to count to one would
 * be a read per user per app-open for nothing.
 */
export async function countSubscriptions(uid: string): Promise<number> {
  const snap = await getDocs(subsCol(uid));
  return snap.size;
}
